import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { GoogleMapContext } from "./context";
import { Theme, useTheme, alpha } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../common/types";
import "./GoogleMapMarker.css";
import { IGoogleMapsMarkerPopup } from "./IGoogleMapsMarkerPopup";
import { makeStyles } from "@material-ui/styles";
import { InfoPopupArrowPosition, MapMarkerImperativeControls } from "./types";

interface Props {
  lat: number;
  lng: number;
  bearing?: number;
  vehicleName?: string;
  infoWindowContent?: React.ReactElement<any>;
  infoWindowArrowPosition?: InfoPopupArrowPosition;
  onClick?: () => void;
  hide?: boolean;
  iconPath?: string;
  markerColor?: string;
}

interface InfoWindowListenerData {
  infoWindow: IGoogleMapsMarkerPopup;
  listener: google.maps.MapsEventListener;
}

const useStyles = makeStyles((theme: Theme) => ({
  markerLabel: {
    fontWeight: "bold",
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    padding: "2px",
  },
}));

export const GoogleMapMarker = forwardRef<MapMarkerImperativeControls, Props>(
  (
    {
      lat,
      lng,
      vehicleName,
      bearing,
      infoWindowContent,
      infoWindowArrowPosition = "top",
      onClick,
      hide,
      iconPath = "M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z",
      markerColor,
    }: Props,
    ref
  ) => {
    const { map } = useContext(GoogleMapContext);
    const theme: Theme = useTheme();
    if (!markerColor) {
      markerColor = theme.palette.primary.main;
    }
    const themeName = useSelector((state: AppState) => state.theme);
    const [markerState, setMarkerState] = useState<google.maps.Marker>();
    const classes = useStyles();
    const hoverRef = useRef<boolean>(false);

    useImperativeHandle<any, MapMarkerImperativeControls>(
      ref,
      () => ({
        setPopupShown(shown: boolean) {
          hoverRef.current = false;
        },
      }),
      []
    );

    const getMarkerLabel: () =>
      | google.maps.MarkerLabel
      | undefined = useCallback(() => {
      return vehicleName
        ? {
            color: theme.palette.text.secondary,
            text: vehicleName,
            className: classes.markerLabel,
          }
        : undefined;
    }, [vehicleName, classes, theme]);

    useEffect(() => {
      if (!map) {
        return;
      }

      let marker: google.maps.Marker;
      if (!markerState) {
        marker = new google.maps.Marker({
          position: { lat, lng },
          map: hide ? null : map,
          title: vehicleName,
          label: getMarkerLabel(),
          draggable: false,
          icon: {
            path: iconPath,
            anchor: new google.maps.Point(12, 12),
            scaledSize: new google.maps.Size(50, 50),
            fillColor: markerColor,
            fillOpacity: 1,
            rotation: bearing,
            strokeWeight: 1,
            strokeColor: theme.palette.text.primary,
          },
        });

        setMarkerState(marker);
      } else {
        markerState.setOptions({ map: hide ? null : map });
        const oldPosition = markerState.getPosition();
        const oldHeadingDirection = (markerState.getIcon() as google.maps.Symbol)
          .rotation;
        const newHeadingDirection = bearing;
        const newPosition = { lat, lng };
        marker = markerState;

        if (
          newPosition.lat !== oldPosition?.lat() ||
          newPosition.lng !== oldPosition?.lng()
        ) {
          marker.setOptions({
            position: newPosition,
          });
        }

        if (oldHeadingDirection !== newHeadingDirection) {
          marker.setOptions({
            icon: {
              ...(marker.getIcon() as google.maps.Symbol),
              rotation: newHeadingDirection,
            },
          });
        }

        marker.setOptions({
          position: { lat, lng },
          label: getMarkerLabel(),
          icon: {
            ...(marker.getIcon() as google.maps.Symbol),
            fillColor: markerColor,
            strokeColor: theme.palette.text.primary,
          },
        });
      }

      let mouseOverListener: InfoWindowListenerData | undefined = undefined;
      let mouseOutListener: InfoWindowListenerData | undefined = undefined;
      let mouseClickListener:
        | google.maps.MapsEventListener
        | undefined = undefined;

      if (onClick) {
        mouseClickListener = markerState?.addListener("click", () => {
          onClick();
        });
      }

      if (infoWindowContent) {
        import("./GoogleMapMarkerPopup").then((x) => {
          const popup = new x.GoogleMapMarkerPopup({
            lat,
            lng,
            paperColor: theme.palette.background.paper,
            content: infoWindowContent,
            arrowPosition: infoWindowArrowPosition,
          });

          mouseOverListener = {
            infoWindow: popup,
            listener: marker.addListener("mouseover", () => {
              popup.setMap(map);
              hoverRef.current = true;
            }),
          };
          mouseOutListener = {
            infoWindow: popup,
            listener: marker.addListener("mouseout", () => {
              popup.setMap(null);
              hoverRef.current = false;
            }),
          };

          // Is mouse pointer hovering during rerender? If so, continue displaying the popup.
          if (hoverRef.current) {
            popup.setMap(map);
          }
        });
      }

      return () => {
        import("./GoogleMapMarkerPopup").then((_) => {
          if (infoWindowContent && !mouseOverListener) {
            console.warn(
              `Not removing the existing info window content! Vehicle '${vehicleName}'`
            );
          }

          if (mouseOverListener) {
            mouseOverListener.infoWindow.setMap(null);
          }

          if (mouseOutListener) {
            google.maps.event.removeListener(mouseOutListener.listener);
          }

          if (mouseOverListener) {
            google.maps.event.removeListener(mouseOverListener.listener);
          }
        });

        if (mouseClickListener) {
          google.maps.event.removeListener(mouseClickListener);
        }

        if (hide) {
          marker.setMap(null);
        }
      };
    }, [
      map,
      vehicleName,
      lat,
      lng,
      bearing,
      theme,
      themeName,
      infoWindowContent,
      onClick,
      markerState,
      infoWindowArrowPosition,
      getMarkerLabel,
      hide,
      iconPath,
      markerColor,
    ]);

    return null;
  }
);
